import type { Ajv } from "ajv";
import Ajv2020 from "ajv/dist/2020";
import addFormats from "ajv-formats";
import { uniq } from "lodash-es";

import { PropertyValue, SubSchema } from "src/api/flowTypes";
import {
  normalizeJsonSchema,
  SchemaNormalizer,
} from "src/jsonSchema/normalizeJsonSchema";
import {
  getSchemaNodesFromUnion,
  isEmptySchema,
} from "src/jsonSchema/schemaUtils";
import {
  isArrayWithItems,
  isObjectWithProperties,
  isRealUnion,
  isUnion,
  JsonSchema,
  JsonSchemaNode,
} from "src/jsonSchema/types";
import { JsonRefPathPrefix } from "src/schema/jsonRefUtils";

const internalSchemaDefKeys = [
  "FeaturesGlobal",
  "Features@",
  "Entities@",
  "case_properties@",
  "Events@",
  "Address",
  "IpAddress",
  "LatLong",
  "Url",
  "File",
] as const;

export const isInternalSchemaDefKey = (key: string) => {
  if (key.startsWith(JsonRefPathPrefix)) {
    key = key.replace(JsonRefPathPrefix, "");
  }
  return internalSchemaDefKeys.some(
    (internalKey) =>
      key === internalKey || (key.includes("@") && key.startsWith(internalKey)),
  );
};

export type ValidationError = {
  type: "json" | "structure" | "refs" | "custom";
  message: string;
};

export type CustomValidator = (
  schema: JsonSchema,
  path?: string,
) => ValidationError | null;

export type ValidationConfig = {
  customValidators?: CustomValidator[];
  ajvValidator?: Ajv;
  internalSubschemas?: Record<string, SubSchema<PropertyValue>>;
  normalizers?: SchemaNormalizer[];
  allowEmpty?: boolean;
};

export const isEmptySchemaString = (jsonString: string): boolean => {
  const trimmed = jsonString.trim();
  if (trimmed === "") {
    return true;
  }
  try {
    const parsed = JSON.parse(trimmed) as JsonSchema;
    return isEmptySchema(parsed);
  } catch {
    return false;
  }
};

export const createDefaultAjv = (allowUnionTypes: boolean = false): Ajv => {
  const ajv = new Ajv2020({
    allErrors: true,
    strict: true,
    strictTypes: true,
    allowUnionTypes,
  });
  addFormats(ajv);

  // Register custom formats (annotation-only, no runtime validation)
  ajv.addFormat("file-id", true);

  // Whitelist custom keywords
  ajv.addKeyword("sensitive");
  ajv.addKeyword("order");

  return ajv;
};

export const validateJsonSchema = (
  jsonString: string,
  config: ValidationConfig = {},
): ValidationError | null => {
  const {
    customValidators = [],
    ajvValidator = createDefaultAjv(),
    internalSubschemas = {},
    normalizers = [],
    allowEmpty = true,
  } = config;

  if (isEmptySchemaString(jsonString)) {
    if (allowEmpty) {
      return null;
    }
    return { type: "structure", message: "Empty schema is not allowed" };
  }

  // Validate JSON object structure
  let parsed: JsonSchema;
  try {
    parsed = JSON.parse(jsonString) as JsonSchema;
  } catch (err: unknown) {
    const message = err instanceof Error ? err.message : String(err);
    return { type: "json", message: `Invalid JSON: ${message}` };
  }

  // Apply normalizers for validation (in memory only, does not mutate original)
  const normalizedForValidation = normalizeJsonSchema(parsed, normalizers);

  // Build schema with internal subschemas for AJV validation
  const parsedWithInternalSubschemas: JsonSchema = {
    ...normalizedForValidation,
    $defs: {
      ...internalSubschemas,
      ...normalizedForValidation.$defs,
    },
  };

  // Run custom validators on normalized schema
  for (const validator of customValidators) {
    const error = validator(normalizedForValidation);
    if (error) {
      return {
        type: error.type,
        message: `Validation error: ${error.message}`,
      };
    }
  }

  if (ajvValidator) {
    // Delete $schema to prevent Ajv from trying to resolve the $schema URI and fail
    const parsedSchema = parsedWithInternalSubschemas;
    const schemaUri = parsedSchema.$schema;
    if (schemaUri) {
      delete parsedSchema.$schema;
    }

    // Validate against JSON schema meta-schema
    try {
      const isValidSchema = ajvValidator.validateSchema(parsedSchema);
      if (!isValidSchema && ajvValidator.errors?.length) {
        const err = ajvValidator.errors[0];
        return {
          type: "structure",
          message: `Schema error at ${err.schemaPath}: ${err.message}`,
        };
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : String(err);
      return {
        type: "structure",
        message: `Schema error: ${message}`,
      };
    }

    // Compile to catch missing $refs
    try {
      ajvValidator.compile(parsedSchema);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : String(err);
      return {
        type: "refs",
        message: `Schema error: ${message}`,
      };
    }

    // Restore previously deleted schema URI (if any)
    if (schemaUri) {
      parsedSchema.$schema = schemaUri;
    }
  }

  return null; // valid
};

// - Custom validators

export const noRealUnions: CustomValidator = (
  schema: JsonSchema,
  path = "#",
) => {
  return traverseSchema(
    schema,
    (node, currentPath) => {
      if (isRealUnion(node)) {
        const hasAnyOf = "anyOf" in node && node.anyOf;
        const unionType = hasAnyOf ? "anyOf" : "oneOf";
        return {
          type: "custom",
          message: `Real union types are not allowed. Found ${unionType} with multiple non-null types at ${currentPath}`,
        };
      }
      return null;
    },
    path,
  );
};

export const noAllOf: CustomValidator = (schema: JsonSchema, path = "#") => {
  return traverseSchema(
    schema,
    (node, currentPath) => {
      if ("allOf" in node && node.allOf) {
        return {
          type: "custom",
          message: `allOf is not allowed at ${currentPath}`,
        };
      }
      return null;
    },
    path,
  );
};

export const noInternalSchemaDefs: CustomValidator = (
  schema: JsonSchema,
  path = "#",
) => {
  const defsPath = `${path}/$defs/`;

  return traverseSchema(
    schema,
    (_node, currentPath) => {
      if (currentPath.startsWith(defsPath)) {
        const normalizedPath = currentPath.replace(defsPath, "");
        if (isInternalSchemaDefKey(normalizedPath)) {
          return {
            type: "custom" as const,
            message: `${currentPath} is not allowed due to conflict with internal subschemas.`,
          };
        }
      }
      return null;
    },
    path,
  );
};

export const noNullOnlyTypes: CustomValidator = (
  schema: JsonSchema,
  path = "#",
) => {
  return traverseSchema(
    schema,
    (node, currentPath) => {
      const isUnion =
        currentPath.includes("anyOf") || currentPath.includes("oneOf");
      if (
        typeof node === "object" &&
        node !== null &&
        "type" in node &&
        !isUnion
      ) {
        const type = Array.isArray(node.type) ? uniq(node.type) : [node.type];
        if (type.length === 1 && type[0] === "null") {
          return {
            type: "custom" as const,
            message: `${currentPath} is not supported because it only allows null values.`,
          };
        }
      }

      const isUnionNode =
        node !== null &&
        typeof node === "object" &&
        ("oneOf" in node || "anyOf" in node);

      if (isUnionNode) {
        const members = node.anyOf ?? node.oneOf ?? [];
        const memberTypes = uniq(
          members
            .flatMap((member) => {
              if ("type" in member) {
                return member.type;
              }
              if ("$ref" in member) {
                return member["$ref"];
              }
              return null;
            })
            .filter(Boolean),
        );
        // checks if the union only contains null values
        // length 0 is already handled by the json schema validator
        if (memberTypes.length === 1 && memberTypes[0] === "null") {
          return {
            type: "custom" as const,
            message: `${currentPath} is not supported because it only allows null values.`,
          };
        }
      }
      return null;
    },
    path,
  );
};

/**
 * Validator to prevent nested object types in schemas.
 * Disallows:
 * - $ref references to object definitions
 * - Inline object definitions with properties
 *
 * Use case: Input schemas should only have primitive types and simple structures.
 */
export const noNestedObjects: CustomValidator = (
  schema: JsonSchema,
  path = "#",
) => {
  return traverseSchema(
    schema,
    (node, currentPath) => {
      // Only check property definitions (not the root schema or $defs)
      const isPropertyDefinition =
        currentPath.includes("/properties/") &&
        !currentPath.includes("/$defs/");

      if (!isPropertyDefinition) {
        return null;
      }

      // Check for $ref to object definitions
      if (node.$ref) {
        return {
          type: "custom",
          message: `Property at ${currentPath} uses a reference ($ref). Input schemas cannot reference object definitions. Use primitive types instead.`,
        };
      }

      // Check for inline object with properties
      if (isObjectWithProperties(node)) {
        return {
          type: "custom",
          message: `Property at ${currentPath} defines a nested object with properties. Input schemas cannot have nested objects. Use primitive types instead.`,
        };
      }

      // Check for union types containing objects
      if (isUnion(node)) {
        const members = getSchemaNodesFromUnion(node);

        for (const member of members) {
          if (isObjectWithProperties(member)) {
            return {
              type: "custom",
              message: `Property at ${currentPath} contains a union with nested objects. Input schemas cannot have nested objects.`,
            };
          }
          if (member.$ref) {
            return {
              type: "custom",
              message: `Property at ${currentPath} contains a union with references. Input schemas cannot reference object definitions.`,
            };
          }
        }
      }

      // Check for arrays with object items
      if (isArrayWithItems(node) && !Array.isArray(node.items)) {
        const items = node.items as JsonSchemaNode;

        // Check if items is a $ref
        if (items.$ref) {
          return {
            type: "custom",
            message: `Property at ${currentPath} is an array with referenced items. Input schemas cannot reference object definitions.`,
          };
        }

        // Check if items is an object with properties
        if (isObjectWithProperties(items)) {
          return {
            type: "custom",
            message: `Property at ${currentPath} is an array of objects with properties. Input schemas cannot have nested objects.`,
          };
        }
      }

      return null;
    },
    path,
  );
};

// - Traversal helper

type VisitorFunc = (
  node: JsonSchemaNode,
  path: string,
) => ValidationError | null;

// Pre-order DFS helper for invoking a visitor function on every node of the schema tree
const traverseSchema = (
  schema: JsonSchema,
  visitorFunc: VisitorFunc,
  initialPath = "#",
): ValidationError | null => {
  const traverse = (
    obj: JsonSchemaNode,
    currentPath: string,
  ): ValidationError | null => {
    if (!obj || typeof obj !== "object") {
      return null;
    }

    const nodeError = visitorFunc(obj, currentPath);
    if (nodeError) {
      return nodeError;
    }

    for (const key in obj) {
      const value = (obj as Record<string, unknown>)[key];
      if (typeof value !== "object" || value === null) {
        continue; // skip non-object values
      }
      const newPath = `${currentPath}/${key}`;
      const error = traverse(value as JsonSchemaNode, newPath);
      if (error) {
        return error;
      }
    }

    return null;
  };

  return traverse(schema, initialPath);
};
